import './login.css';
import Form from 'react-bootstrap/Form';
import FaceIcon from '../images/face-icon.png';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/header';
import { Link } from 'react-router-dom';
import { getDomain, persistAuthSession, postData } from '../utils/apis/api'; // Import the postData function from api.js
import Loader from '../components/loader';
import CustomToaster from '../components/custom-toaster';

const Login = () => {
  const navigate = useNavigate();
  const [emailId, setEmailId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [imageSrc, setImageSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState(''); // 'success' or 'error'

  // Fetch the user icon on component mount
  useEffect(() => {
    const fetchIcon = async () => {
      const userType = sessionStorage.getItem('userType');

      if (userType !== 'admin') {
        try {
          const data = {
            agent_name: sessionStorage.getItem('agentName'),
            type_: 'icon',
            filename: `${sessionStorage.getItem('agentName')}.png`,
          };

          // Make the API call to fetch the icon
          const response = await postData('/fetch-icon', data);

          if (response) {
            // Prefix the Base64 data with the appropriate MIME type
            setImageSrc(`data:image/png;base64,${response}`);
          } else {
            throw new Error('Invalid response');
          }
        } catch (error) {
          console.error('Error fetching user icon:', error);
        }
      } else {
        // If userType is admin, set the FaceIcon
        setImageSrc(FaceIcon); // Default icon for admin
      }
    };

    // Check if userType is not present
    if (!sessionStorage.getItem('userType')) {
      setImageSrc(FaceIcon);
    } else {
      fetchIcon();
    }
  }, []);

  // Handle email input change
  const handleEmailChange = (event) => {
    setEmailId(event.target.value);
    setError(''); // Clear error when typing
  };

  // Handle password input change
  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    setError(''); // Clear error when typing
  };

  // Handle login button click
  const handleLogin = async () => {
    if (!emailId || !password) {
      setError('Username and password are required');
      return;
    }

    setIsLoading(true); // Show Loader
    setShowToast(false); // Hide previous toast
    setToastMessage(''); // Clear previous message

    try {
      const data = { username: emailId, password: password };

      // Define headers
      const headers = {
        'Content-Type': 'application/json',
        "Accept": 'application/json',
      };

      // Make auth call against the root API so agent-prefixed sessions also
      // receive refresh tokens from the shared auth endpoint.
      const loginResponse = await fetch(`${getDomain()}login`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      const response = await loginResponse.json().catch(() => ({}));

      if (!loginResponse.ok) {
        throw new Error(response.detail || 'Login failed');
      }

      if (response.access_token) {
        persistAuthSession(response);

        // Show success toast
        setToastMessage('Login successful!');
        setToastVariant('success');
        setShowToast(true);

        // Navigate to /chat after a delay
        setTimeout(() => {
          navigate('/chat');
        }, 2000);
      } else {
        // Handle unexpected responses without access_token
        throw new Error('Unexpected API response');
      }
    } catch (error) {
      console.error('Login failed:', error);

      // Extract error message from the API response
      let errorMsg = 'An unexpected error occurred. Please try again later.';
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail; // Extract the "detail" field
      } else if (error.message) {
        errorMsg = error.message; // Use the error message from the catch block
      }

      // Show error toast with the extracted error message
      setToastMessage(errorMsg);
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setIsLoading(false); // Hide Loader
    }
  };

  return (
    <>
      <Header />
      <div className="LoginMain">
        <div className="LoginContainer">
          <img
            src={
              sessionStorage.getItem('userType')
                ? sessionStorage.getItem('userType') === 'admin'
                  ? FaceIcon
                  : imageSrc
                : FaceIcon
            }
            alt="icon"
            className="gpt-icon"
          />
          <h2 className="my-4 LoginText">Sign in to Tranquilla AI</h2>
          <Form.Control
            value={emailId}
            onChange={handleEmailChange}
            className="my-3"
            type="email"
            placeholder="Login Username*"
            required
          />
          <Form.Control
            value={password}
            onChange={handlePasswordChange}
            className="my-3"
            type="password"
            placeholder="Password*"
            required
          />
          {error && <p className="text-danger">{error}</p>}
          <div className="col-12 text-end">
            <button className="forgot-password">
              <Link
                to={((sessionStorage.getItem('userType') === 'user') && sessionStorage.getItem('agentName')) ? "/forgot-password?agentName=" + sessionStorage.getItem('agentName') : "/forgot-password"}
              >Forgot Password?</Link>
            </button>
          </div>
          <button
            onClick={handleLogin}
            disabled={!emailId || !password}
            className="ContinueButton col-12"
          >
            {isLoading ? 'Logging in...' : 'Continue'}
          </button>
          <h6 className="mt-2">
            Click here to <Link
              to={((sessionStorage.getItem('userType') === 'user') && sessionStorage.getItem('agentName')) ? "/signup?agentName=" + sessionStorage.getItem('agentName') : "/signup"}
            >Sign up</Link>
          </h6>
          <a href="mailto:support@tranquilla.ai" className="tech-support-link">
            Need more help? Contact Us.
          </a>
        </div>
      </div>
      <footer className="footer">

      </footer>

      {/* Loader */}
      {isLoading && <Loader />}

      {/* Custom Toast */}
      {toastMessage && (
        <CustomToaster
          show={showToast}
          onClose={() => setShowToast(false)}
          message={toastMessage}
          type={toastVariant}
        />
      )}
    </>
  );
};

export default Login;
